import React, { useState, useEffect } from 'react';
import { Plus, Send, Phone, MessageCircle, Mail, Zap, Shuffle, Hand, CheckCircle2 } from 'lucide-react';
import api from '../lib/api';
import { formatDate } from '../mock';
import { toast } from '../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';

export const origens = ['Loja física', 'Telefone', 'WhatsApp', 'Site', 'Instagram', 'Facebook', 'Indicação'];
export const resultados = ['Em atendimento', 'Agendou test-drive', 'Proposta', 'Comprou', 'Sem interesse'];

const resultadoStyle = {
  'Em atendimento': 'text-sky-400 bg-sky-400/10',
  'Agendou test-drive': 'text-violet-400 bg-violet-400/10',
  'Proposta': 'text-amber-400 bg-amber-400/10',
  'Comprou': 'text-emerald-400 bg-emerald-400/10',
  'Sem interesse': 'text-muted-foreground bg-muted/40',
};

const origemStyle = {
  'Instagram': 'text-pink-400 bg-pink-400/10', 'Site': 'text-sky-400 bg-sky-400/10',
  'WhatsApp': 'text-emerald-400 bg-emerald-400/10', 'Indicação': 'text-purple-400 bg-purple-400/10',
  'Facebook': 'text-blue-400 bg-blue-400/10', 'Loja física': 'text-orange-400 bg-orange-400/10',
  'Telefone': 'text-teal-400 bg-teal-400/10',
};

const empty = { nome: '', telefone: '', whatsapp: '', email: '', origem: 'Loja física', veiculoInteresse: '', vendedor: '', resultado: 'Em atendimento', observacoes: '' };

const BdcAtendimentos = () => {
  const [list, setList] = useState([]);
  const [team, setTeam] = useState([]);
  const [modo, setModo] = useState('manual');
  const [dOpen, setDOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try {
      const [a, t, s] = await Promise.all([api.get('/atendimentos'), api.get('/team'), api.get('/settings')]);
      setList(a.data); setTeam(t.data); setModo(s.data.distribuicaoModo);
    } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const toggleModo = async () => {
    const next = modo === 'auto' ? 'manual' : 'auto';
    setModo(next);
    try { await api.put('/settings', { distribuicaoModo: next }); toast({ title: 'Distribuição atualizada', description: next === 'auto' ? 'Automática (round-robin)' : 'Manual' }); } catch (e) {}
  };

  const handleSave = async () => {
    if (!form.nome || (!form.telefone && !form.whatsapp)) {
      toast({ title: 'Dados obrigatórios', description: 'Informe nome e um contato (telefone ou WhatsApp).' });
      return;
    }
    setSaving(true);
    try {
      await api.post('/atendimentos', form);
      setForm(empty); setDOpen(false); await load();
      toast({ title: 'Atendimento registrado', description: 'Cliente adicionado à base do BDC.' });
    } catch (e) { toast({ title: 'Erro ao salvar' }); } finally { setSaving(false); }
  };

  const enviarCrm = async (a) => {
    try {
      await api.post(`/atendimentos/${a.id}/enviar-crm`);
      await load();
      toast({ title: 'Enviado para o CRM', description: `${a.nome} entrou no funil de vendas.` });
    } catch (e) {
      toast({ title: 'Não foi possível enviar', description: e?.response?.data?.detail || 'Tente novamente.' });
    }
  };

  const changeResultado = async (a, resultado) => {
    setList((prev) => prev.map((x) => x.id === a.id ? { ...x, resultado } : x));
    try { await api.put(`/atendimentos/${a.id}`, { ...a, resultado }); } catch (e) { load(); }
  };

  const input = 'w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/50';

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">{list.length} atendimentos · {list.filter((a) => !a.enviadoCrm).length} pendentes</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={toggleModo} className={`flex items-center gap-2 px-3.5 py-2.5 rounded-lg text-sm font-medium border transition-colors ${modo === 'auto' ? 'border-primary/50 bg-primary/10 text-foreground' : 'border-border text-foreground/65 hover:text-foreground'}`}>
            {modo === 'auto' ? <Shuffle className="w-4 h-4 text-primary" /> : <Hand className="w-4 h-4" />}
            Distribuição: {modo === 'auto' ? 'Automática' : 'Manual'}
          </button>
          <Dialog open={dOpen} onOpenChange={setDOpen}>
            <DialogTrigger asChild>
              <button className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors whitespace-nowrap"><Plus className="w-4 h-4" /> Novo atendimento</button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border text-foreground max-w-lg">
              <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase">Novo atendimento</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4 py-2">
                <div className="col-span-2"><label className="text-xs text-muted-foreground mb-1.5 block">Nome do cliente</label><input className={input} value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Nome completo" /></div>
                <div><label className="text-xs text-muted-foreground mb-1.5 block">Telefone</label><input className={input} value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} placeholder="(11) 90000-0000" /></div>
                <div><label className="text-xs text-muted-foreground mb-1.5 block">WhatsApp</label><input className={input} value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="(11) 90000-0000" /></div>
                <div className="col-span-2"><label className="text-xs text-muted-foreground mb-1.5 block">E-mail</label><input className={input} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="cliente@email.com" /></div>
                <div><label className="text-xs text-muted-foreground mb-1.5 block">Origem</label><select className={input} value={form.origem} onChange={(e) => setForm({ ...form, origem: e.target.value })}>{origens.map((o) => <option key={o} className="bg-popover">{o}</option>)}</select></div>
                <div><label className="text-xs text-muted-foreground mb-1.5 block">Veículo de interesse</label><input className={input} value={form.veiculoInteresse} onChange={(e) => setForm({ ...form, veiculoInteresse: e.target.value })} placeholder="Ex: Corolla" /></div>
                <div><label className="text-xs text-muted-foreground mb-1.5 block">Vendedor {modo === 'auto' && <span className="text-primary">(auto)</span>}</label><select className={input} value={form.vendedor} onChange={(e) => setForm({ ...form, vendedor: e.target.value })}><option value="" className="bg-popover">{modo === 'auto' ? 'Automático' : 'Selecione'}</option>{team.map((t) => <option key={t.id} value={t.nome} className="bg-popover">{t.nome}</option>)}</select></div>
                <div><label className="text-xs text-muted-foreground mb-1.5 block">Resultado</label><select className={input} value={form.resultado} onChange={(e) => setForm({ ...form, resultado: e.target.value })}>{resultados.map((r) => <option key={r} className="bg-popover">{r}</option>)}</select></div>
                <div className="col-span-2"><label className="text-xs text-muted-foreground mb-1.5 block">Observações</label><textarea className={input} rows={2} value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} placeholder="Notas do atendimento" /></div>
              </div>
              <DialogFooter>
                <button onClick={() => setDOpen(false)} className="px-4 py-2.5 rounded-lg text-sm font-medium border border-border text-foreground/65 hover:text-foreground transition-colors">Cancelar</button>
                <button onClick={handleSave} disabled={saving} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 transition-colors disabled:opacity-60">{saving ? 'Salvando...' : 'Registrar'}</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="rounded-xl border border-border/70 bg-card p-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground text-xs tracking-wider border-b border-border/70">
              <th className="pb-3 font-semibold">CLIENTE</th>
              <th className="pb-3 font-semibold hidden lg:table-cell">CONTATO</th>
              <th className="pb-3 font-semibold">ORIGEM</th>
              <th className="pb-3 font-semibold hidden md:table-cell">INTERESSE</th>
              <th className="pb-3 font-semibold hidden sm:table-cell">VENDEDOR</th>
              <th className="pb-3 font-semibold">RESULTADO</th>
              <th className="pb-3 font-semibold text-right">CRM</th>
            </tr>
          </thead>
          <tbody>
            {list.map((a) => (
              <tr key={a.id} className="border-b border-border/70 hover:bg-muted/40 transition-colors">
                <td className="py-3.5">
                  <p className="font-medium">{a.nome}</p>
                  <p className="text-[11px] text-muted-foreground">{formatDate((a.dataHora || '').split(' ')[0])} · {(a.dataHora || '').split(' ')[1]}</p>
                </td>
                <td className="py-3.5 hidden lg:table-cell">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    {a.telefone && <a href={`tel:${a.telefone}`} className="hover:text-foreground"><Phone className="w-3.5 h-3.5" /></a>}
                    {a.whatsapp && <a href={`https://wa.me/${a.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="hover:text-emerald-400"><MessageCircle className="w-3.5 h-3.5" /></a>}
                    {a.email && <a href={`mailto:${a.email}`} className="hover:text-foreground"><Mail className="w-3.5 h-3.5" /></a>}
                    <span className="text-[11px]">{a.telefone || a.whatsapp}</span>
                  </div>
                </td>
                <td className="py-3.5"><span className={`text-[11px] px-2 py-1 rounded-full font-medium ${origemStyle[a.origem] || 'text-muted-foreground bg-muted/40'}`}>{a.origem}</span></td>
                <td className="py-3.5 text-foreground/75 hidden md:table-cell">{a.veiculoInteresse || '—'}</td>
                <td className="py-3.5 text-foreground/75 hidden sm:table-cell">{a.vendedor || <span className="text-muted-foreground/60">—</span>}</td>
                <td className="py-3.5">
                  <select value={a.resultado} onChange={(e) => changeResultado(a, e.target.value)} className={`text-[11px] font-semibold px-2 py-1 rounded-full outline-none cursor-pointer border-0 ${resultadoStyle[a.resultado] || 'text-foreground/65 bg-muted/40'}`}>
                    {resultados.map((r) => <option key={r} value={r} className="bg-popover text-foreground">{r}</option>)}
                  </select>
                </td>
                <td className="py-3.5 text-right">
                  {a.enviadoCrm ? (
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-400"><CheckCircle2 className="w-3.5 h-3.5" /> No CRM</span>
                  ) : (
                    <button onClick={() => enviarCrm(a)} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-primary hover:text-foreground border border-primary/40 hover:border-primary rounded-lg px-2.5 py-1.5 transition-colors"><Send className="w-3.5 h-3.5" /> Enviar</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.length === 0 && <div className="text-center py-14 text-muted-foreground">Nenhum atendimento registrado.</div>}
      </div>
    </div>
  );
};

export default BdcAtendimentos;
